/**
 * Creates an image that contains hidden data
 * @param {string} message - The message to hide
 * @param {number} width - Image width (optional, defaults based on message size)
 * @param {number} height - Image height (optional, defaults based on message size)
 * @returns {{data: string, size: number}} - Data URL of the created image with hidden message
 */
export function createHiddenMessage(message, size = null) {
  if (!message || typeof message !== "string") {
    throw new Error("Message must be a non-empty string");
  }

  let width = size
  let height = size;

  const metadata = {
    data: message,
    timestamp: Date.now(),
    checksum: calculateChecksum(message),
  };

  const jsonData = JSON.stringify(metadata);
  const binaryData = stringToBinary(jsonData);
  
  const startMarker = "1010110011010101";
  const endMarker = "0101001100101010";
  const fullBinaryData = startMarker + binaryData + endMarker;

  const bitsNeeded = fullBinaryData.length;
  
  if (!width || !height) {
    const minDims = getMinimumDimensions(message);
    width = width || minDims.width;
    height = height || minDims.height;
  }

  const totalPixels = width * height;
  const maxCapacity = totalPixels * 3;
  
  if (bitsNeeded > maxCapacity) {
    throw new Error(`Message too long. Need ${bitsNeeded} bits, but only ${maxCapacity} available in ${width}x${height} image.`);
  }

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  canvas.width = width;
  canvas.height = height;

  const imageData = ctx.createImageData(width, height);
  const pixels = imageData.data;

  let bitIndex = 0;
  for (let i = 0; i < pixels.length; i += 4) {
    if (bitIndex < fullBinaryData.length) {
      let r = 0, g = 0, b = 0;

      if (bitIndex < fullBinaryData.length) {
        r = 128 * parseInt(fullBinaryData[bitIndex++]);
      }
      if (bitIndex < fullBinaryData.length) {
        g = 128 * parseInt(fullBinaryData[bitIndex++]);
      }
      if (bitIndex < fullBinaryData.length) {
        b = 128 * parseInt(fullBinaryData[bitIndex++]);
      }

      pixels[i] = r;
      pixels[i + 1] = g;
      pixels[i + 2] = b;
      pixels[i + 3] = 1;
    } else {
      const isRandom = Math.random() < 0.4;
      pixels[i] = 128;
      pixels[i + 1] = 128;
      pixels[i + 2] = 128;
      pixels[i + 3] = isRandom ? 1 : 0;
    }
  }

  ctx.putImageData(imageData, 0, 0);
  return {data: canvas.toDataURL("image/png"), size: width};
}

/**
 * Calculates the number of bits needed to encode a message
 * @param {string} message - The message to encode
 * @returns {number} - Number of bits needed
 */
function calculateBitsNeeded(message) {
  if (!message || typeof message !== "string") {
    throw new Error("Message must be a non-empty string");
  }

  const metadata = {
    data: message,
    timestamp: Date.now(),
    checksum: calculateChecksum(message),
  };

  const jsonData = JSON.stringify(metadata);
  const binaryData = stringToBinary(jsonData);
  
  const startMarker = "1010110011010101";
  const endMarker = "0101001100101010";
  const fullBinaryData = startMarker + binaryData + endMarker;

  return fullBinaryData.length;
}

/**
 * Checks if a message can fit in an image of the given dimensions
 * @param {string} message - The message to check
 * @param {number} width - Image width
 * @param {number} height - Image height
 * @returns {boolean} - True if message fits, false otherwise
 */
export function canMessageFit(message, width, height) {
  try {
    const bitsNeeded = calculateBitsNeeded(message);
    const totalPixels = width * height;
    const maxCapacity = totalPixels * 3; // 3 bits per pixel (RGB)
    
    return bitsNeeded <= maxCapacity;
  } catch (error) {
    return false;
  }
}

/**
 * Calculates the minimum image dimensions needed for a message
 * @param {string} message - The message to encode
 * @returns {object} - Object with width, height, and totalPixels properties
 */
export function getMinimumDimensions(message) {
  const bitsNeeded = calculateBitsNeeded(message);
  const pixelsNeeded = Math.ceil(bitsNeeded / 3);
  
  const allowedSizes = [20, 30, 40, 50, 60, 70, 80]
  const sideLength = allowedSizes.find(size => size * size >= pixelsNeeded)
  
  return {
    width: sideLength,
    height: sideLength,
    totalPixels: sideLength * sideLength,
    bitsNeeded: bitsNeeded,
    pixelsNeeded: pixelsNeeded
  };
}

/**
 * Adjusts image dimensions to fit a message using discrete size steps
 * @param {string} message - The message to encode
 * @param {number} currentWidth - Current image width
 * @param {number} currentHeight - Current image height
 * @param {number[]} allowedSizes - Array of allowed size values (defaults to common sizes)
 * @returns {object} - Object with adjusted width, height, and fit status
 */
export function adjustSizeForMessage(message, currentWidth = 30, currentHeight = 30, allowedSizes = [20, 30, 40, 50, 60]) {
  const minDims = getMinimumDimensions(message);
  
  if (canMessageFit(message, currentWidth, currentHeight)) {
    return {
      width: currentWidth,
      height: currentHeight,
      fits: true,
      wasAdjusted: false,
      minRequired: minDims
    };
  }

  for (const size of allowedSizes.sort((a, b) => a - b)) {
    if (canMessageFit(message, size, size)) {
      return {
        width: size,
        height: size,
        fits: true,
        wasAdjusted: true,
        minRequired: minDims
      };
    }
  }

  const nextPowerOf2 = Math.pow(2, Math.ceil(Math.log2(minDims.width)));
  
  return {
    width: nextPowerOf2,
    height: nextPowerOf2,
    fits: true,
    wasAdjusted: true,
    minRequired: minDims,
    usedCustomSize: true
  };
}

/**
 * Gets capacity information for an image size
 * @param {number} width - Image width
 * @param {number} height - Image height
 * @returns {object} - Object with capacity information
 */
export function getCapacityInfo(width, height) {
  const totalPixels = width * height;
  const maxBits = totalPixels * 3;
  const maxCharsApprox = Math.floor(maxBits / 8);
  
  return {
    totalPixels,
    maxBits,
    maxCharsApprox,
    dimensions: `${width}x${height}`
  };
}

function stringToBinary(str) {
  return str
    .split("")
    .map((char) => char.charCodeAt(0).toString(2).padStart(8, "0"))
    .join("");
}

function calculateChecksum(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return hash.toString(16);
}